import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { BarcodeFormat, BarcodeScanner } from '@capacitor-mlkit/barcode-scanning';
import { BrowserMultiFormatReader, IScannerControls } from '@zxing/browser';
import { BarcodeFormat as ZXingBarcodeFormat, DecodeHintType } from '@zxing/library';

export const CAMERA_SCAN_ENABLED_KEY = 'camera_scan_enabled';

const NATIVE_FORMATS: BarcodeFormat[] = [
  BarcodeFormat.Ean13,
  BarcodeFormat.Ean8,
  BarcodeFormat.Code128,
  BarcodeFormat.Code39,
  BarcodeFormat.QrCode,
  BarcodeFormat.Pdf417,
  BarcodeFormat.DataMatrix,
  BarcodeFormat.UpcA,
  BarcodeFormat.UpcE,
];

// Web/PWA (@zxing/library) — mirrors NATIVE_FORMATS. Restricting the decoders
// the reader tries per frame is the main speed win over the default (all formats).
const WEB_FORMATS: ZXingBarcodeFormat[] = [
  ZXingBarcodeFormat.EAN_13,
  ZXingBarcodeFormat.EAN_8,
  ZXingBarcodeFormat.CODE_128,
  ZXingBarcodeFormat.CODE_39,
  ZXingBarcodeFormat.QR_CODE,
  ZXingBarcodeFormat.PDF_417,
  ZXingBarcodeFormat.DATA_MATRIX,
  ZXingBarcodeFormat.UPC_A,
  ZXingBarcodeFormat.UPC_E,
];

// Rear camera, high resolution so small EAN/QR decode reliably.
const WEB_CAMERA_CONSTRAINTS: MediaStreamConstraints = {
  audio: false,
  video: {
    facingMode: { ideal: 'environment' },
    width: { ideal: 1280, min: 640 },
    height: { ideal: 720, min: 480 },
    aspectRatio: { ideal: 1.7778 },
  },
};

@Injectable({ providedIn: 'root' })
export class CameraScannerService {

  // ── Enabled state (persisted in localStorage) ─────────────────────────────
  private readonly _enabled$ = new BehaviorSubject<boolean>(
    localStorage.getItem(CAMERA_SCAN_ENABLED_KEY) === 'true'
  );
  readonly enabled$ = this._enabled$.asObservable();

  // ── Web overlay visibility ─────────────────────────────────────────────────
  private readonly _showOverlay$ = new BehaviorSubject<boolean>(false);
  readonly showOverlay$ = this._showOverlay$.asObservable();

  // ── Internal state ─────────────────────────────────────────────────────────
  private _videoElement: HTMLVideoElement | null = null;
  private _webScannerControls: IScannerControls | null = null;
  private readonly _cancelWebScan$ = new Subject<string | null>();

  // ── Settings ───────────────────────────────────────────────────────────────

  isEnabled(): boolean {
    return this._enabled$.getValue();
  }

  setEnabled(value: boolean): void {
    localStorage.setItem(CAMERA_SCAN_ENABLED_KEY, String(value));
    this._enabled$.next(value);
  }

  // ── Video element — registered by CameraOverlayComponent ────────────────

  registerVideoElement(el: HTMLVideoElement): void {
    this._videoElement = el;
  }

  // ── Permissions ────────────────────────────────────────────────────────────

  async requestPermissions(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      // Browser permissions are requested automatically by getUserMedia
      return true;
    }
    try {
      const { camera } = await BarcodeScanner.requestPermissions();
      return camera === 'granted' || camera === 'limited';
    } catch {
      return false;
    }
  }

  // ── Scan ───────────────────────────────────────────────────────────────────

  startScan(): Observable<string | null> {
    if (Capacitor.isNativePlatform()) {
      return this._startNativeScan();
    }
    return this._startWebScan();
  }

  /**
   * Cancels an active scan (web or native).
   * The scan Observable will emit null and complete.
   */
  stopScan(): void {
    if (Capacitor.isNativePlatform()) {
      BarcodeScanner.stopScan().catch(() => { });
    } else {
      this._cancelWebScan$.next(null);
    }
  }

  // ── Native (Android / iOS via ML Kit) ─────────────────────────────────────

  private _startNativeScan(): Observable<string | null> {
    return new Observable(observer => {
      BarcodeScanner.scan({ formats: NATIVE_FORMATS })
        .then(result => {
          const value = result.barcodes?.[0]?.rawValue ?? null;
          observer.next(value);
          observer.complete();
        })
        .catch(err => observer.error(err));
    });
  }

  // ── Web / PWA (via @zxing/browser) ────────────────────────────────────────

  private _startWebScan(): Observable<string | null> {
    return new Observable(observer => {
      if (!this._videoElement) {
        observer.error(new Error('[CameraScannerService] No video element registered. Make sure <app-camera-overlay> is present in AppComponent.'));
        return;
      }

      this._showOverlay$.next(true);

      const hints = new Map<DecodeHintType, unknown>();
      hints.set(DecodeHintType.POSSIBLE_FORMATS, WEB_FORMATS);

      const reader = new BrowserMultiFormatReader(hints, {
        delayBetweenScanAttempts: 100,   // default is 500ms → only ~2 attempts/sec
        delayBetweenScanSuccess: 500,
      });

      // Listen for manual cancel (user taps ✕ on overlay)
      const cancelSub = this._cancelWebScan$.subscribe(value => {
        this._cleanupWebReader(reader);
        observer.next(value);
        observer.complete();
      });

      reader
        .decodeFromConstraints(WEB_CAMERA_CONSTRAINTS, this._videoElement, (result, _err) => {
          if (result) {
            cancelSub.unsubscribe();
            this._cleanupWebReader(reader);
            observer.next(result.getText());
            observer.complete();
          }
          // _err is NotFoundException on every frame with no barcode — expected, ignore
        })
        .then(controls => {
          this._webScannerControls = controls;
        })
        .catch(err => {
          cancelSub.unsubscribe();
          this._showOverlay$.next(false);
          observer.error(err);
        });

      // Teardown on unsubscribe
      return () => {
        cancelSub.unsubscribe();
        this._cleanupWebReader(reader);
      };
    });
  }

  private _cleanupWebReader(_reader?: BrowserMultiFormatReader): void {
    if (this._webScannerControls) {
      this._webScannerControls.stop();
      this._webScannerControls = null;
    }
    this._showOverlay$.next(false);
  }
}
